//store representing invoices and rates
import { defineStore } from 'pinia'
import type { Invoice, Project } from '@/types/types'
import { useProjectStore, useAlertStore, useCacheStore } from '@/stores'
import { invoiceSchema, lineItemSchema  } from '@/types/types'
import { useStoreSyncer } from '@/composables/Store-syncer';
import router from '@/router'
import * as yup from 'yup'
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import api from '@/libs/axios';
import { ApiError, type ApiResponse } from '@/libs/axios';


export const useInvoiceStore = defineStore('invoices', () => {
  const cache = useCacheStore();
  const alert = useAlertStore(); 
  const loading = ref(false);
  const error = ref(null);
  const { fetchAll } = useStoreSyncer('/invoices', 'invoices'); 


  const allInvoices = computed(()=>{
    const allProjects = cache.getFromCache('projects') || [];
    return allProjects.map(proj =>{
      return proj.invoices
    }).flat();
  })

  const tableData = computed(()=>{
    //const projectStore = useProjectStore();
    //const data = cache.getFromCache('invoices') || [];
    const mapped = allInvoices.value.map((inv) => {
    let temp = inv;
    try{
      temp.customerName = inv.customer.invoiceName;
      //temp.engineer = projectStore.getById(inv.projectId).engineer;
     //temp.engineer = inv.project.engineer;
    } catch (err){
      console.error("..trapped error mapping customerName on Invoice-table.parseTableData...err = " + err)
      console.error("inv = " + JSON.stringify(inv));
      temp.customerName = 'empty';
    }
    const formattedDate = inv.datePaid != null ? new Date(inv.datePaid).toLocaleDateString() : null
    temp.datePaid = formattedDate
    return temp;
  });
  return mapped;
  })

  /**
   * fetch all invoices from backend, store in cache
   *
   */
  async function fetchAllInvoices() {
    console.log('invoice store fetching all invoices from backend...')
    //add engineer field from projects
    const projectStore = useProjectStore();
    const allProjects = await projectStore.fetchAllProjects();
    return allProjects.map(proj =>{
      const invoices = proj.invoices;
      return invoices.map(inv =>{
        inv.engineer = proj.engineer;
        return inv;
      })
      //return temp;
    }).flat();
  }


  /**
   * get by invoice id
   */
  function getById(id: string) {
    console.log(`invoices store.getById(${id})....fetching from cache...`)
    try {
      const found : Invoice = allInvoices.value.find(inv =>{
        return inv.id === id;
      })
      if(!found){
        throw new Error('invoice ' + id + ' not found')
      }
      return found;
      //return await parseIncoming(invoice)
    } catch (error) {
      console.error('error on invoice-store.getById : ' + error)
    }
    //return { id: 'empty' }
  }

  /**
   * return all invoices assigned to project
   * @param projectId
   * @return Array<Invoice>
   */
  async function getProjectInvoices(projectId: number) {
    console.log('invoices store.getProjectInvoices(' + projectId + ')...')
    loading.value = true;
    try {
      const projectStore = useProjectStore();
      const project = await projectStore.getById(projectId);
      const invoices = project.invoices //array
      loading.value = false;
      return invoices
      //return await parseIncoming(project);
    } catch (error) {
      console.error(error)
      alert.error("invoice-store.getProjectInvoices error : " + error);
    }
    loading.value = false;
    return [{ id: 'empty' }]
    // console.log("doing nothing...");
  }

  /**
   * update a single invoice
   * @return Array<Invoice> the array of projectInvoices
   */
  async function updateInvoice(inv: Invoice) {
    //const projectId = parseInt(inv.projectId);
    console.log('invoices store.updateInvoice()...')


    //schema for updating invoice, no projectId needed!
    const schema = yup.object({
      //address: yup.string().defined(),
      // projectId: yup.string().length(8).defined(),
      // province: yup.mixed().oneOf(['AB', 'BC', 'SK', 'MB', 'ON', 'NB', 'NS']).defined(),
      //taxRate: yup.number().min(0).max(1).positive().defined(),
      lineItems: yup.array().of(lineItemSchema).defined().min(1),
      customer_id: yup.number(),
      datePaid: yup.date().nullable(),
      owner_id: yup.number(),
      engineer_id: yup.number()
    })

    //return await update(inv,schema,'invoice');
    const projects = useProjectStore();
    loading.value = true;

    try{
      const parsed = await schema.validate(inv, { stripUnknown: true })
      const resp : ApiResponse<Invoice> = await api.put(`/invoices/${inv.id}`, parsed);
      const updated : Invoice = resp.data.data['invoice'];    //TODO ensure API returns all graphFetched relations
      const projBefore : Project = projects.getById(updated.projectId);
      const InvArrAfter = projBefore.invoices.map(invoice =>{
        return invoice.id === updated.id ? updated : invoice;
      })
      projBefore.invoices = InvArrAfter;
      await projects.writeChanges(projBefore);

      alert.success(`updated Invoice ${updated.id} succesful`);

      await new Promise(resolve => setTimeout(()=>{
        alert.clear()
          loading.value = false;
          return updated;
      }, 3000));
        
        
    }catch(err){
        console.log("error in invoice-store.updateInvoice()..")
          error.value = err.toString();
        if(err.name == 'ValidationError'){
          console.log('validation error : ' + err)
          alert.error('validation error : ' + err)
          setTimeout(() => {
              alert.clear()
              loading.value = false;
          }, 3000)
        }else if(err instanceof ApiError){
          console.log("put to /invoices trapped APIerror" + err.message);
          alert.error("put to /invoices trapped APIerror" + err.message);
        }else{
          console.log("error with updating invoice object : " + err);
          alert.error("error with updating invoice object : " + err);
        }
        loading.value = false;
        if(err instanceof ApiError){
            alert.error(err.message)
        }else{
            alert.error("error with updating invoice.." + err);
        }
    }

  }

  /**
   * delete an invoice
   */
  async function deleteInvoice(id: Number) {
    console.log('invoices store deleteInvoice for id : ' + id)
    //return await remove(id);
    const projects = useProjectStore();
    //return await remove(id);
    loading.value = true;
    let resp : ApiResponse<Invoice>;
    try{
      resp = await api.delete(`/invoices/${id}`);
      const deleted : Invoice = resp.data.data['deleted'];
      //console.log("..deleted postech = " + JSON.stringify(deleted));
      const projBefore : Project = projects.getById(deleted.projectId);
      //console.log("... deleted.id = " + deleted.id);
      const InvArr = projBefore.invoices.filter(invoice=>{
          //console.log("...proj invoice.id = " + invoice.id)
          return invoice.id != deleted.id;
      });
      projBefore.invoices = InvArr;
      //console.log("...postechArr after filter = " + JSON.stringify(postechArr));
      projects.writeChanges(projBefore);

    }catch(err){
      console.log("..error in invoice-store.deleteInvoice()...");
      if(err instanceof ApiError){
          console.log(".delete trapped APIerror" + err.message);
          throw err;
      }else{
          console.log("error with deleting invoice object : " + err);
          alert.error("error with deleting invoice object : " + err);
      }
    }
  }

  /**
   * create a new invoice
   * backend will assign the invoiceId and return it
   */
  async function createInvoice(inv: Invoice) {
    console.log('invoice store createInvoice : ' + JSON.stringify(inv))

     //schema for creating invoice
    const schema = yup.object({
      address: yup.string().defined(),
       projectId: yup.string().length(8).defined(),
       province: yup.mixed().oneOf(['AB', 'BC', 'SK', 'MB', 'ON', 'NB', 'NS']).defined(),
      taxRate: yup.number().min(0).max(1).positive().defined(),
      lineItems: yup.array().of(lineItemSchema).defined().min(1),
      customer_id: yup.number().required(),
     // customerId: yup.number().required().transform((value, originalValue)=> {
     //   return this.parent.customer.id;
     // }),
     //customerId : yup.number().ref('customer')
      //customerId : yup.number().test('isvalid', 'isnotvalid',(value, context) => this.parent.customer.id),
      datePaid: yup.date().nullable().default(null),
     // owner: yup.string(),
      created_by : yup.string().email().defined(),
     // engineer : yup.string().email(),
      owner_id : yup.number().required(),
      engineer_id : yup.number().required()

    })

    const projects = useProjectStore();
    loading.value = true;

    try{
      const parsed = await schema.validate(inv, { stripUnknown: true })
      delete parsed['engineer'];
      const resp : ApiResponse<Invoice> = await api.post(`/invoices/`, parsed);
      const created : Invoice = resp.data.data['invoice'];
      //console.log("..added postech = " + JSON.stringify(created));
      const projBefore : Project = projects.getById(created.projectId);
      projBefore.invoices.push(created);
      //console.log("... created.id = " + created.id);
      await projects.writeChanges(projBefore);
      await new Promise(resolve => setTimeout(()=>{
        alert.clear()
        loading.value = false;
        router.push(`/projects/${created.projectId}/invoices/${created.id}`);
      }, 3000));

    }catch(err){
      console.log("..error in invoice-store.createInvoice()...");
      error.value = err.toString();
      if(err.name == 'ValidationError'){
        console.log('validation error : ' + err)
        alert.error('validation error : ' + err)
        setTimeout(() => {
            alert.clear()
            loading.value = false;
        }, 3000)
      }else if(err instanceof ApiError){
          console.log("post to /invoices trapped APIerror" + err.message);
          alert.error("post to /invoices trapped APIerror" + err.message);
      }else{
          console.log("error with creating invoice object : " + err);
          alert.error("error with creating invoice object : " + err);
      }

    }
  }

  /**
   * parse incoming invoice object, modify schema?
   *
  async function parseIncoming(invoice: Invoice) {
    console.log("invoice-store.parseIncoming(): " + JSON.stringify(invoice))
   return await validateForm(invoice, invoiceSchema);

 
}*/

  /**
   * parse outgoing invoice object
   *
  function parseOutgoing(invoice: Invoice, isCreate: boolean = false) {
    const parsed = {} //invoice;
    parsed.lineItems = invoice.lineItems
    parsed.customerId = invoice.customer.id
    parsed.owner = invoice.owner

    if (isCreate) {
      parsed.projectId = invoice.projectId
      parsed.province = invoice.province
      parsed.taxRate = invoice.taxRate //TODO backend calcs this?
      parsed.address = invoice.address
    } else {
      parsed.datePaid = invoice.datePaid
      //remove id
    }
    console.log('parsed invoice outgoing : ...')
    console.log(JSON.stringify(parsed))
    return parsed
  }*/


  /**
   * form validation TODO move this to composable / class
   *
  async function validateForm(object, schema){
    console.log("..validating form..");
    //try form validation
    const alert = useAlertStore()
    let parsed
    try {
      parsed = await schema.validate(object, { stripUnknown: true }) //remove extra fields
      console.log('parsed = ' + JSON.stringify(parsed))
      return parsed;
    } catch (err) {
      //err.name; // => 'ValidationError'
      //err.errors; // => ['Deve ser maior que 18']
      console.error("error in form validation : " + err)
      alert.error(err)
      setTimeout(() => {
        alert.clear()
      }, 3000)
      return null;
    }
  }*/

  /**
   * fetch the table data from the view in the DB
   */
  async function fetchTableData(){
    console.log("invoice store fetchTableData()..");
    loading.value = true;
    const response = await api.get('/invoices/view');
    loading.value = false;
    console.log("...response = " + response.data.status + " : " + response.data.message);
    return response.data.data.invoices;
  }

  async function getReport(){
    console.log("invoice store getReport()..");
    try{
        const resp = await api.get(`/invoices/report`);
        const CSVdata = new Blob([resp.data], { type: 'text/csv' });
        const filename = 'invoice_report.csv';
        // Create to temporary link to initiate
        // download process
        let temp_link = document.createElement('a')

        // Download csv file
        temp_link.download = filename;
        let url = window.URL.createObjectURL(CSVdata);
        temp_link.href = url

        // This link should not be displayed
        temp_link.style.display = 'none'
        document.body.appendChild(temp_link)

        // Automatically click the link to trigger download
        temp_link.click()
        document.body.removeChild(temp_link)
        //return resp.data.data.report;
    }catch (err){
      console.log('error in invoice getReport : ' + err);
    }
  }

  return {
   // getAll,
    fetchAllInvoices,
    getById,
    getProjectInvoices,
    updateInvoice,
    deleteInvoice,
    createInvoice,
    allInvoices,
    loading,
    tableData,
    getReport,
    fetchTableData
    //getFileName
  }
})
